import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Api } from '../../services/api';
import { Subscription, finalize, map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../services/loader';

interface AuditLog {
  genai_issue_audit_log_id: number;
  issue_id: number;
  pflm_id: number;
  field_name: string | null;
  previous_value: string | null;
  updated_value: string | null;
  updated_by: string | null;
  updated_date: string; // ISO string
  issue_status?: string | null;
  risk_severity?: string | null;
  impact?: string | null;
  business_impact?: string | null;
  current_state?: string | null;
  fte?: string | null;
  revenue_impact?: string | null;
  margin_impact?: string | null;
  overall_summary?: string | null;
  comments?: string | null;
  reason?: string | null;
  issue_name?: string | null;         // <--- used for heading
  issue_description?: string | null;
}

interface LatestChangeRow {
  field: string;
  before: string | null;
  after: string | null;
  updatedDate: Date;
  updatedBy: string | null;
}

@Component({
  selector: 'app-supervisor-update-logs',
  imports: [CommonModule],
  templateUrl: './supervisor-update-logs.html',
  styleUrl: './supervisor-update-logs.scss',
})
export class SupervisorUpdateLogs implements OnInit {
  private subscription: any = [];

  logsLoading = false;
  logsError: string | null = null;
  latestChanges: LatestChangeRow[] = [];
  issueName: string | null = null;

  pflmId: number = 0;

  constructor(public bsModalRef: BsModalRef, private apiService: Api,
    private loaderService: LoaderService) { }

  ngOnInit() {

    this.apiService.supervisorUpdateLogContext$.subscribe(ctx => {
      if (!ctx.issueId || !ctx.pflmId) return; // wait until both present
      const payload = {
        IssueIds: [ctx.issueId],
        PFLMId: ctx.pflmId
      };

      this.fetchSupervisorUpdateLogs(payload);
    });

  }

  ngOnDestroy() {
    let subscription: Subscription;
    if (this.subscription?.length)
      for (subscription of this.subscription) {
        subscription.unsubscribe();
      }
  }


  fetchSupervisorUpdateLogs(payload: { IssueIds: number[], PFLMId: number }): void {
    this.loaderService.show();
    this.subscription['fetchAuditLogsAPI'] = this.apiService
      .fetchAuditLogsAPI(payload)
      .subscribe({
        next: (res: any) => {
          this.loaderService.hide();
          const logs = res?.logs || [];

          // SIMPLE: Get issue_name from first log
          this.issueName = logs.length ? logs[0].issue_name : null;

          // Keep existing transform logic
          this.latestChanges = this.transformLogs(logs);
        },
        error: (err) => {
          this.loaderService.hide();
          console.error('[AuditLog] Fetch failed →', err);
        }
      });
  }

  private transformLogs(logs: AuditLog[]): LatestChangeRow[] {
    // Fixed order for UI
    const ORDER = [
      'Total FTEs',
      'Revenue Impact (Million $)',
      'Margin Impact (Million $)',
      'Reason',
      'Business Impact',
      'Updated Date',
      'Updated By'
    ];

    // Helper: get latest log for any of the provided field names
    const getLatest = (names: string[]): AuditLog | null => {
      let latest: AuditLog | null = null;
      for (const log of logs) {
        if (!log.field_name) continue;
        if (!names.includes(log.field_name)) continue;
        if (!latest || new Date(log.updated_date) > new Date(latest.updated_date)) {
          latest = log;
        }
      }
      return latest;
    };

    // 1) Total FTEs (API may send "FTE" or "Total FTEs")
    const fte = getLatest(['FTE', 'Total FTEs']);

    // 2) Revenue Impact
    const revenue = getLatest(['Revenue Impact']);

    // 3) Margin Impact
    const margin = getLatest(['Margin Impact']);

    // 4) Reason
    const reason = getLatest(['Reason']);

    // 5) Business Impact
    const bizImpact = getLatest(['Business Impact']);

    // 6) Updated date by → derive from overall latest & second-latest across all logs
    const sortedByDate = [...logs]
      .filter(l => !!l.updated_date)
      .sort((a, b) => +new Date(b.updated_date) - +new Date(a.updated_date));
    const latestOverall = sortedByDate[0] ?? null;
    const secondLatestOverall = sortedByDate[1] ?? null;

    // Build rows in the exact order with empty strings when missing
    const rows: LatestChangeRow[] = [
      {
        field: 'Total FTEs',
        before: fte?.previous_value ?? '',
        after: fte?.updated_value ?? '',
        updatedDate: fte ? new Date(fte.updated_date) : new Date(0),
        updatedBy: fte?.updated_by ?? null
      },
      {
        field: 'Revenue Impact<br><span class="sub-label">(Million $)</span>',
        before: revenue?.previous_value ?? '',
        after: revenue?.updated_value ?? '',
        updatedDate: revenue ? new Date(revenue.updated_date) : new Date(0),
        updatedBy: revenue?.updated_by ?? null
      },
      {
        field: 'Margin Impact<br><span class="sub-label">(Million $)</span>',
        before: margin?.previous_value ?? '',
        after: margin?.updated_value ?? '',
        updatedDate: margin ? new Date(margin.updated_date) : new Date(0),
        updatedBy: margin?.updated_by ?? null
      },
      {
        field: 'Reason',
        before: reason?.previous_value ?? '',
        after: reason?.updated_value ?? '',
        updatedDate: reason ? new Date(reason.updated_date) : new Date(0),
        updatedBy: reason?.updated_by ?? null
      },
      {
        field: 'Business Impact',
        before: bizImpact?.previous_value ?? '',
        after: bizImpact?.updated_value ?? '',
        updatedDate: bizImpact ? new Date(bizImpact.updated_date) : new Date(0),
        updatedBy: bizImpact?.updated_by ?? null
      },
      {
        field: 'Updated Date',
        // Use required format dd/MM/yyyy HH:mm:ss
        before: secondLatestOverall ? this.formatDateIST(secondLatestOverall.updated_date) : '',
        after: latestOverall ? this.formatDateIST(latestOverall.updated_date) : '',
        updatedDate: latestOverall ? new Date(latestOverall.updated_date) : new Date(0),
        updatedBy: latestOverall?.updated_by ?? null
      },

      {
        field: 'Updated By',
        before: secondLatestOverall?.updated_by ?? '',
        after: latestOverall?.updated_by ?? '',
        updatedDate: latestOverall
          ? new Date(latestOverall.updated_date)
          : new Date(0),
        updatedBy: latestOverall?.updated_by ?? null
      }
    ];

    // return rows;

    return rows.filter(row => {
      const beforeHasValue = row.before?.toString().trim();
      const afterHasValue = row.after?.toString().trim();

      return beforeHasValue || afterHasValue;
    });

  }

  // Format: 19/02/2026 09:49:42
  // private formatDateDDMMYYYYHHMMSS(d: Date): string {
  //   const pad = (n: number) => String(n).padStart(2, '0');
  //   const dd = pad(d.getDate());
  //   const mm = pad(d.getMonth() + 1);
  //   const yyyy = d.getFullYear();
  //   const HH = pad(d.getHours());
  //   const MM = pad(d.getMinutes());
  //   const SS = pad(d.getSeconds());
  //   return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
  // }

  private formatDateIST(dateString: string): string {
    const utcDate = new Date(dateString + 'Z');

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(utcDate);

    const get = (type: string) =>
      parts.find(p => p.type === type)?.value ?? '';

    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} IST`;
  }

  trackByField(_index: number, row: LatestChangeRow) {
    return row.field;
  }
}
